import { useNavigate } from "react-router-dom";
import { createContext, useContext, useEffect, useState } from "react";

import { IUser } from "@/types";
import { getCurrentUser } from "@/lib/appwrite/api";
import { isUserBanned } from "@/lib/appwrite/userViolationAPI";
import { useToast } from "@/components/ui/use-toast";
import { useSignOutAccount } from "@/lib/react-query/queries";

export const INITIAL_USER = {
  id: "",
  name: "",
  username: "",
  emailOrPhone: "",
  imageUrl: "",
  bio: "",
  acceptedSLA: false,
};

const INITIAL_STATE = {
  user: INITIAL_USER,
  isLoading: false,
  isAuthenticated: false,
  setUser: () => {},
  setIsAuthenticated: () => {},
  checkAuthUser: async () => false as boolean,
};

type IContextType = {
  user: IUser;
  isLoading: boolean;
  setUser: React.Dispatch<React.SetStateAction<IUser>>;
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  checkAuthUser: () => Promise<boolean>;
};

const AuthContext = createContext<IContextType>(INITIAL_STATE);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<IUser>(INITIAL_USER);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: signOut } = useSignOutAccount();
  const { toast } = useToast();

  const clearSession = () => {
    localStorage.removeItem('authToken');
    setUser(INITIAL_USER);
    setIsAuthenticated(false);
  };

  const checkAuthUser = async () => {
    setIsLoading(true);
    try {
      const currentAccount = await getCurrentUser();
      
      const userIsBanned = await isUserBanned(currentAccount!.$id);
      if (userIsBanned) {
              toast({
                title: "Account Suspended",
                description: "Your account is under ban due to repeated violations of our community guidelines. Contact contact@busiconn.com for assistance.",
                variant: "destructive"
              });
              signOut();
              setIsAuthenticated(false);
              setUser(INITIAL_USER);
              navigate("/sign-in?banned=true");
              return false;
      }
      if (!currentAccount) {
        clearSession();
        return false;
      }
      setUser({
        id: currentAccount.$id,
        name: currentAccount.name,
        username: currentAccount.username,
        emailOrPhone: currentAccount.emailOrPhone,
        imageUrl: currentAccount.imageUrl,
        bio: currentAccount.bio,
        acceptedSLA: currentAccount.acceptedSLA
      });
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error(error);
      clearSession();
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const isValid = await checkAuthUser();
      if (!isValid) {
        navigate('/sign-in', { replace: true });
      }
    };

    initAuth();
  }, []);

  const value = {
    user,
    setUser,
    isLoading,
    isAuthenticated,
    setIsAuthenticated,
    checkAuthUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useUserContext = () => useContext(AuthContext);
